import React, { useEffect, useState, useMemo } from 'react';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import ToggleButton from '@mui/material/ToggleButton';
import Alert from '@mui/material/Alert';

import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import useStyles from '../styles';
import Header from '../components/Header';
import { useAppDispatch, useAppSelector } from '../state/store';
import {
  getCurrentLocation,
  getCurrentLocationChildLocations,
  getLocSensorStatusData,
  getSensorsById,
  getSelectedVars,
} from '../state/selectors';
import { setSensorsById, setSensorVars } from '../state/actions';
import { VarName } from '../utils/varNames';
import { knownOfflineTag } from '../Widgets/OfflineView/helpers';
import { BasicSensorType, DataTreeItem, SensorLatest } from '../services/api';
import {
  fetchAllSensorsInLocation,
  fetchSensorLatest,
  updateSensorProperties,
} from '../services/apiService';
import Loading from '../components/Loading';
import LocationRow from '../Widgets/OfflineView/LocationRow';

export enum ListOptions {
  all = 'all',
  untagged = 'untagged',
}

// --- Main Component ---
function OfflineView(): JSX.Element {
  const theme = useTheme();
  const { classes } = useStyles();
  const dispatch = useAppDispatch();
  const isDesktopView = useMediaQuery(theme.breakpoints.up('sm'));

  // Selectors
  const selectedVars = useAppSelector(getSelectedVars);
  const currentLocation = useAppSelector(getCurrentLocation);
  const currentLocationChildLocations = useAppSelector(getCurrentLocationChildLocations);
  const locSensorStatusData = useAppSelector(getLocSensorStatusData);
  const sensorsById = useAppSelector(getSensorsById);

  const [listType, setListType] = useState(ListOptions.all);
  const [fetchingSensorProps, setFetchingSensorProps] = useState(true);
  const [isTagging, setIsTagging] = useState(false);

  // Ensure OnlineStatus and BatteryLevelPct variable is selected
  useEffect(() => {
    if (
      selectedVars.length > 0 &&
      (!selectedVars.includes(VarName.OnlineStatus) ||
        !selectedVars.includes(VarName.BatteryLevelPct))
    ) {
      const varsToAdd = [...selectedVars];
      if (!varsToAdd.includes(VarName.OnlineStatus)) varsToAdd.push(VarName.OnlineStatus);
      if (!varsToAdd.includes(VarName.BatteryLevelPct)) varsToAdd.push(VarName.BatteryLevelPct);
      dispatch(setSensorVars(varsToAdd));
    }
  }, [dispatch, selectedVars]);

  // fetch sensory type and its gateway properties for all sensors in location
  useEffect(() => {
    fetchAllSensorsInLocation(currentLocation, ['type', 'gateway', 'tags'])
      .then((sensors) => {
        setFetchingSensorProps(false);
        const sensorProps = sensors.sensors ?? [];
        dispatch(setSensorsById(sensorProps as SensorLatest[]));
      })
      .catch(() => setFetchingSensorProps(false));
  }, [currentLocation, dispatch]);

  // Data Preparation
  const allOnlineData = locSensorStatusData.onlineData.get(VarName.OnlineStatus);

  // filter retired and virtual sensors
  const allSensors = useMemo(() => {
    const filteredRetiredSensors =
      allOnlineData?.filter((item) => item?.value !== undefined && item.value > -2) ?? [];
    const filteredVirtualSensors = filteredRetiredSensors.filter(
      (sensor) => sensorsById.get(sensor.id)?.type !== BasicSensorType.Virtual
    );
    return filteredVirtualSensors;
  }, [allOnlineData, sensorsById]);

  const currentLocOfflineSensors = useMemo(() => {
    const locSensors =
      allSensors.filter(
        (item) => item.location === currentLocation && item?.value !== undefined && item.value <= 0
      ) ?? [];
    return locSensors;
  }, [allSensors, currentLocation]);

  // Determine locations to render (use sublocations as tree if available)
  const sortedChildLocations = useMemo(() => {
    if (currentLocationChildLocations.length === 0) return [];
    return [...currentLocationChildLocations]
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
      .map((loc) => loc.id);
  }, [currentLocationChildLocations]);

  // Calculate globally "Fully Offline" locations to avoid duplication in nested views.
  const fullyOfflineLocationIds = useMemo(() => {
    const offlineIds = new Set<string>();

    sortedChildLocations.forEach((childLoc) => {
      const locSensors = allSensors.filter(
        (item) => item.location === childLoc || item.location?.startsWith(`${childLoc}#`)
      );
      const offlineSensors = locSensors.filter(
        (item) => item?.value !== undefined && item.value <= 0
      );

      if (locSensors.length > 0 && locSensors.length === offlineSensors.length) {
        offlineSensors.forEach((item) => {
          if (item.location) offlineIds.add(item.location);
        });
      }
    });
    return offlineIds;
  }, [allSensors, sortedChildLocations]);

  const handleTagging = async (sensors: DataTreeItem[], isTagged: boolean) => {
    setIsTagging(true);

    try {
      await Promise.all(
        sensors.map(async (sensor) => {
          const sensorDetails = await fetchSensorLatest(sensor.id);

          const currentTags = sensorDetails.tags || [];
          const offLineTagExists = currentTags.includes(knownOfflineTag);

          let updatedTags = [...currentTags];

          if (isTagged) {
            updatedTags = currentTags.filter((tag: string) => tag !== knownOfflineTag);
          } else if (!offLineTagExists) {
            updatedTags.push(knownOfflineTag);
          }

          const response = await updateSensorProperties(sensor.id, {
            tags: updatedTags,
          });

          if (response.id) {
            const updatedSensorDetails = {
              ...sensorDetails,
              ...response,
            };

            dispatch(setSensorsById([updatedSensorDetails]));
          }
        })
      );
    } finally {
      setIsTagging(false);
    }
  };

  if (!isDesktopView) return <div className={classes.bodyContent} />;

  return (
    <Box
      className={classes.bodyContent}
      sx={{ overflowY: 'hidden', height: '100%', padding: `0 !important`, position: 'relative' }}
    >
      {isTagging && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255,255,255,0.6)',
            backdropFilter: 'blur(2px)',
            zIndex: 10,
          }}
        >
          <Loading />
        </Box>
      )}
      <Box sx={{ padding: '10px' }}>
        <Header showSearchField={false} />
      </Box>
      <Box sx={{ overflow: 'scroll' }}>
        <Box sx={{ padding: '0 10px 0 5px' }}>
          <ToggleButtonGroup
            color="primary"
            size="small"
            value={listType}
            exclusive
            onChange={(_, value) => value && setListType(value)}
            sx={{ display: 'flex', justifyContent: 'end', marginTop: '8px' }}
          >
            <ToggleButton sx={{ textTransform: 'inherit' }} value={ListOptions.all}>
              Show All
            </ToggleButton>
            <ToggleButton sx={{ textTransform: 'none' }} value={ListOptions.untagged}>
              Show untagged only
            </ToggleButton>
          </ToggleButtonGroup>
          <List sx={{ background: 'none' }}>
            {fetchingSensorProps && <Loading />}
            {/* show summary from current location */}
            {currentLocOfflineSensors.length > 0 && (
              <LocationRow
                locId={currentLocation}
                allSensors={allSensors}
                fullyOfflineLocs={fullyOfflineLocationIds}
                listType={listType}
                handleTagging={handleTagging}
              />
            )}
            {/* show summary from child locations */}
            {sortedChildLocations.map((childLocationId) => (
              <LocationRow
                key={childLocationId}
                locId={childLocationId}
                allSensors={allSensors}
                fullyOfflineLocs={fullyOfflineLocationIds}
                listType={listType}
                handleTagging={handleTagging}
              />
            ))}
            {allSensors.filter((item) => item?.value !== undefined && item.value <= 0).length ===
              0 && <Alert severity="info">Sensors not found</Alert>}
          </List>
        </Box>
      </Box>
    </Box>
  );
}

export default OfflineView;
